/**
 * Device drafts — what a user was typing into a form, kept in this browser until
 * the form is saved.
 *
 * Komala, 2026-09-17: an OTP half keyed in on an iPad was wiped by an accidental
 * pull-down, which reloads the page. iPad Safari never shows the "leave this
 * page?" dialog, so no guard can stop that reload; the only protection is to have
 * the values somewhere the reloaded page can find them. This is that somewhere.
 *
 * One entry per (form, agent): `cid-agent:draft:<formKey>:<agentId>`. The agent id is
 * in the key because a device can be shared — one agent's
 * half-typed OTP must never open under another's login. The phone and desktop
 * shells use the SAME keys, so rotating an iPad across the 1100px switch (which
 * swaps the whole route tree and unmounts the form) brings the typing across.
 *
 * Values go through JSON, so dayjs objects are tagged on the way in and rebuilt
 * on the way out; everything else antd keeps in a form store is plain data.
 *
 * localStorage can be full, disabled or throw (Safari private mode, cleared site
 * data) — every call is wrapped, and a failure only means no device draft.
 */
import dayjs from 'dayjs';
import { viewAs } from '../api/client';

const PREFIX = 'cid-agent:draft:';
const VERSION = 1;
// A device draft nobody came back to in two weeks is not worth resurrecting.
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
const DAYJS_TAG = '__dayjs';

const keyFor = (formKey, userId) => `${PREFIX}${formKey}:${userId || 'anon'}`;

const encode = (value) => {
  if (dayjs.isDayjs(value)) return value.isValid() ? { [DAYJS_TAG]: value.toISOString() } : null;
  if (Array.isArray(value)) return value.map(encode);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, encode(v)]));
  }
  return value;
};

const decode = (value) => {
  if (Array.isArray(value)) return value.map(decode);
  if (value && typeof value === 'object') {
    if (typeof value[DAYJS_TAG] === 'string') return dayjs(value[DAYJS_TAG]);
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, decode(v)]));
  }
  return value;
};

/** Keep `values` (and any page `meta`) for this form on this device. */
export function saveDeviceDraft(formKey, userId, values, meta = {}) {
  // HQ viewing as the agent: nothing can be saved, so nothing typed is kept on
  // HQ's computer either (it would hold the agent's customers' details).
  if (viewAs.active) return null;
  try {
    const entry = { v: VERSION, savedAt: new Date().toISOString(), values: encode(values), meta };
    window.localStorage.setItem(keyFor(formKey, userId), JSON.stringify(entry));
    return entry.savedAt;
  } catch {
    return null;
  }
}

/** The device draft for this form, or null. Expired or unreadable entries are dropped. */
export function loadDeviceDraft(formKey, userId) {
  try {
    const raw = window.localStorage.getItem(keyFor(formKey, userId));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (entry?.v !== VERSION || !entry.savedAt || Date.now() - new Date(entry.savedAt).getTime() > MAX_AGE_MS) {
      window.localStorage.removeItem(keyFor(formKey, userId));
      return null;
    }
    return { savedAt: entry.savedAt, values: decode(entry.values || {}), meta: entry.meta || {} };
  } catch {
    return null;
  }
}

export function clearDeviceDraft(formKey, userId) {
  try {
    window.localStorage.removeItem(keyFor(formKey, userId));
  } catch {
    // nothing to clear
  }
}
